import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import api from '../services/api';
import InfoIconButton from '../components/InfoIconButton';
import './ExtendedServicesReportPage.css';

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatDate(val) {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  return s;
}

/** Table shows district number (e.g. #131); filter uses full school name. */
function formatDistrictDisplay(district) {
  if (district == null || String(district).trim() === '') return '—';
  const s = String(district).trim();
  return /^\d+$/.test(s) ? `#${s}` : s;
}

const COLUMNS = [
  { key: 'sc_name', label: 'SC', sortable: true, filter: true, narrow: true },
  { key: 'client_name', label: 'Client', sortable: true, narrowClient: true },
  { key: 'birthdate', label: 'Birthdate', sortable: true },
  { key: 'district', label: 'District', sortable: true, filter: true, narrow: true },
  { key: 'ifsp_date', label: 'IFSP Date', sortable: true },
  { key: 'school_start', label: 'School Start', sortable: true },
  { key: 'decline_1950', label: '1950 Decline', sortable: true },
  { key: 'family_interested_es', label: 'Family Interest', sortable: true },
];

function compare(a, b, key) {
  const va = a[key];
  const vb = b[key];
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (key === 'birthdate' || key === 'ifsp_date' || key === 'school_start') {
    const da = new Date(va);
    const db = new Date(vb);
    return da.getTime() - db.getTime();
  }
  if (typeof va === 'boolean' && typeof vb === 'boolean') return (va ? 1 : 0) - (vb ? 1 : 0);
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
}

const REPORT_DESCRIPTION = 'Open clients in the Extended Services window (3rd birthday May–Aug). Sort by clicking column headers; use the filter icon on SC to narrow results.';

/**
 * Extended Services Report (legacy option 104). Same data as Admin report; viewable by managers and service coordinators.
 */
export default function ExtendedServicesReportPage({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const [sortKey, setSortKey] = useState('sc_name');
  const [sortDir, setSortDir] = useState('asc');
  const [staffFilter, setStaffFilter] = useState(new Set());
  const [districtFilter, setDistrictFilter] = useState(new Set());
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [draftStaffFilter, setDraftStaffFilter] = useState(new Set());
  const [draftDistrictFilter, setDraftDistrictFilter] = useState(new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 220 });

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      setRoleCode(res.data?.membership?.role_code || null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  const runReport = useCallback(() => {
    setError(null);
    setLoading(true);
    reportsAPI
      .get('extended-services-report', {})
      .then((res) => {
        setRows(res.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load report.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const displayedRows = useMemo(() => {
    let out = rows;
    if (staffFilter.size > 0) {
      out = out.filter((r) => staffFilter.has(String(r.sc_name ?? '').trim() || '(blank)'));
    }
    if (districtFilter.size > 0) {
      out = out.filter((r) => districtFilter.has(String(r.district ?? '').trim() || '(blank)'));
    }
    out = [...out].sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, staffFilter, districtFilter, sortKey, sortDir]);

  const distinctStaff = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(String(r.sc_name ?? '').trim() || '(blank)'));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  /** Unique district options for filter: full name in list, filter by district code. */
  const distinctDistrictOptions = useMemo(() => {
    const byCode = new Map();
    rows.forEach((r) => {
      const code = String(r.district ?? '').trim() || '(blank)';
      if (!byCode.has(code)) byCode.set(code, String(r.district_name ?? '').trim() || code);
    });
    return [...byCode.entries()]
      .map(([district, district_name]) => ({ district, district_name: district_name || district }))
      .sort((a, b) => (a.district_name || '').localeCompare(b.district_name || '', undefined, { sensitivity: 'base' }));
  }, [rows]);

  useEffect(() => {
    if (!filterAnchor || !openFilterColumn) return;
    const r = filterAnchor.getBoundingClientRect();
    const width = openFilterColumn === 'district' ? 380 : 220;
    const padding = 12;
    let left = r.left;
    if (left + width > window.innerWidth - padding) left = window.innerWidth - width - padding;
    if (left < padding) left = padding;
    let top = r.bottom;
    const maxHeight = 320;
    if (top + maxHeight > window.innerHeight - padding) top = Math.max(padding, r.top - maxHeight);
    setPopoverPosition({ top, left, width });
  }, [filterAnchor, openFilterColumn]);

  const openFilter = (colKey, ev) => {
    ev.stopPropagation();
    setFilterAnchor(ev.currentTarget);
    setOpenFilterColumn(colKey);
    if (colKey === 'sc_name') setDraftStaffFilter(new Set(staffFilter));
    else if (colKey === 'district') setDraftDistrictFilter(new Set(districtFilter));
  };

  const applyFilter = () => {
    if (openFilterColumn === 'sc_name') setStaffFilter(new Set(draftStaffFilter));
    else if (openFilterColumn === 'district') setDistrictFilter(new Set(draftDistrictFilter));
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const clearFilter = () => {
    if (openFilterColumn === 'sc_name') {
      setStaffFilter(new Set());
      setDraftStaffFilter(new Set());
    } else if (openFilterColumn === 'district') {
      setDistrictFilter(new Set());
      setDraftDistrictFilter(new Set());
    }
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const toggleDraftStaff = (val) => {
    setDraftStaffFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const toggleDraftDistrict = (districtCode) => {
    setDraftDistrictFilter((prev) => {
      const next = new Set(prev);
      if (next.has(districtCode)) next.delete(districtCode);
      else next.add(districtCode);
      return next;
    });
  };

  const formatCell = (col, row) => {
    const val = row[col.key];
    if (col.key === 'district') return formatDistrictDisplay(val);
    if (col.key === 'sc_name') {
      if (val == null || String(val).trim() === '') return '—';
      const s = String(val).trim();
      const comma = s.indexOf(',');
      return comma >= 0 ? s.slice(0, comma).trim() : s;
    }
    if (col.key === 'birthdate' || col.key === 'ifsp_date' || col.key === 'school_start') return formatDate(val) || '—';
    if (col.key === 'decline_1950' || col.key === 'family_interested_es') return val === true ? 'Yes' : val === false ? 'No' : (val != null && val !== '' ? String(val) : '—');
    return val == null || val === '' ? '—' : String(val);
  };

  const downloadCsv = () => {
    const headers = COLUMNS.map((c) => c.key);
    const lines = [
      headers.join(','),
      ...displayedRows.map((r) =>
        COLUMNS.map((col) => csvEscape(formatCell(col, r))).join(',')
      ),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'extended-services-report.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const hasResults = rows.length > 0;
  const isSc = roleCode === 'SERVICE_COORDINATOR';

  const activeFilterSummary = useMemo(() => {
    const parts = [];
    if (staffFilter.size > 0) {
      const names = [...staffFilter].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      parts.push(`SC: ${names.join(', ')}`);
    }
    if (districtFilter.size > 0) {
      const names = [...districtFilter]
        .map((code) => distinctDistrictOptions.find((o) => o.district === code)?.district_name || code)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      parts.push(`District: ${names.join(', ')}`);
    }
    return parts;
  }, [staffFilter, districtFilter, distinctDistrictOptions]);

  const filterPopover =
    openFilterColumn &&
    createPortal(
      <div
        className="extended-services-report-page-filter-popover"
        style={{
          position: 'fixed',
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          width: `${popoverPosition.width}px`,
        }}
        role="dialog"
        aria-label={openFilterColumn === 'district' ? 'Filter District' : 'Filter SC'}
      >
        <div className="extended-services-report-page-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
        <div className="extended-services-report-page-filter-popover-list extended-services-report-page-filter-popover-list-wide">
          {openFilterColumn === 'sc_name' &&
            distinctStaff.map((val) => (
              <label key={val} className="extended-services-report-page-filter-check">
                <input type="checkbox" checked={draftStaffFilter.has(val)} onChange={() => toggleDraftStaff(val)} />
                <span>{val}</span>
              </label>
            ))}
          {openFilterColumn === 'district' &&
            distinctDistrictOptions.map((opt) => (
              <label key={opt.district} className="extended-services-report-page-filter-check">
                <input
                  type="checkbox"
                  checked={draftDistrictFilter.has(opt.district)}
                  onChange={() => toggleDraftDistrict(opt.district)}
                />
                <span className="extended-services-report-page-filter-district-label">{opt.district_name}</span>
              </label>
            ))}
        </div>
        <div className="extended-services-report-page-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="extended-services-report-page">
      <div className="extended-services-report-page-title-row">
        <h2 className="extended-services-report-page-title">Extended Services Report{titleSuffix}</h2>
        <InfoIconButton
          onClick={() => setInfoOpen((v) => !v)}
          ariaExpanded={infoOpen}
          ariaControls="extended-services-report-page-info"
          ariaLabel="Show report description"
          ariaLabelExpanded="Hide report description"
          className="extended-services-report-page-info-icon"
        />
      </div>
      {infoOpen && (
        <p id="extended-services-report-page-info" className="extended-services-report-page-info-text">
          {REPORT_DESCRIPTION}
        </p>
      )}
      {isSc && staffDisplayName && (
        <p className="extended-services-report-page-context" aria-live="polite">
          Service Coordinator: {staffDisplayName}
        </p>
      )}
      <div className="extended-services-report-page-actions">
        <button type="button" className="btn btn-outline" onClick={() => { setRows([]); setError(null); setStaffFilter(new Set()); setDistrictFilter(new Set()); }}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary extended-services-report-page-download-btn" onClick={downloadCsv} disabled={displayedRows.length === 0}>
          Export CSV
        </button>
        <button type="button" className="btn btn-primary extended-services-report-page-print-btn" onClick={() => window.print()} disabled={displayedRows.length === 0}>
          Print
        </button>
      </div>

      {activeFilterSummary.length > 0 && (
        <p className="extended-services-report-page-filter-summary no-print">
          Filters: {activeFilterSummary.join(' | ')}
        </p>
      )}

      {loading && rows.length === 0 && (
        <p className="extended-services-report-page-loading" aria-live="polite">Loading…</p>
      )}
      {error && (
        <div className="extended-services-report-page-error" role="alert">
          {error}
        </div>
      )}

      {hasResults && (
        <div className="extended-services-report-page-results">
          <p className="extended-services-report-page-count">
            {displayedRows.length} client{displayedRows.length !== 1 ? 's' : ''}
            {(staffFilter.size > 0 || districtFilter.size > 0) && ' (filtered)'}.
          </p>
          <div className="extended-services-report-page-table-wrap">
            <table className="extended-services-report-page-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSortClick(col.key)}
                      className={`${col.sortable ? 'extended-services-report-page-sortable' : ''} ${col.narrow ? (col.key === 'district' ? 'extended-services-report-page-col-district' : '') : ''} ${col.key === 'sc_name' ? 'extended-services-report-page-col-sc' : ''} ${col.narrowClient ? 'extended-services-report-page-col-client' : ''}`.trim()}
                    >
                      <div className="extended-services-report-page-header-cell">
                        <span className="extended-services-report-page-header-label">
                          {col.label}
                          {col.sortable && sortKey === col.key && (
                            <span className="extended-services-report-page-sort-arrow" aria-hidden="true">
                              {sortDir === 'asc' ? ' ↑' : ' ↓'}
                            </span>
                          )}
                        </span>
                        {col.filter && (
                          <span className="extended-services-report-page-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`extended-services-report-page-filter-trigger ${(col.key === 'sc_name' ? staffFilter : districtFilter).size > 0 ? 'is-active' : ''}`}
                              onClick={(e) => openFilter(col.key, e)}
                              aria-label={`Filter ${col.label}`}
                            >
                              <svg className="extended-services-report-page-filter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row, idx) => (
                  <tr key={idx}>
                    {COLUMNS.map((col) => (
                      <td key={col.key} className={`${col.key === 'sc_name' ? 'extended-services-report-page-col-sc' : ''} ${col.key === 'district' ? 'extended-services-report-page-col-district' : ''} ${col.narrowClient ? 'extended-services-report-page-col-client' : ''}`.trim()}>
                        {formatCell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {filterPopover}
    </div>
  );
}
